import React, { useState, useEffect } from "react";
import Typography from "@components/typography";
import Column from "@components/column";
import Row from "@components/row";
import styled from "@emotion/styled";
import {
  Star,
  Phone,
  Mail,
  MapPin,
  Clock,
  FileText,
  Users,
  Truck,
  TrendingUp,
  Shield,
  Award,
  Heart,
  Package,
  ShoppingCart,
  DollarSign,
  Calendar,
  BarChart3,
  Target,
  CreditCard,
  ShieldCheck,
  MessageCircle,
  ThumbsUp,
  Eye,
  Edit,
  Download,
  Printer,
  Bike,
  Car,
  UserCheck,
  IdCard,
  BadgeCheck,
  AlertTriangle,
  ZoomOut,
  ZoomIn,
  RotateCw,
  Bell,
  Wallet,
} from "lucide-react";
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from "recharts";
import { useModal } from "@context/ModalContext";
import WalletAdjustmentModal from "@components/modals/WalletAdjustmentModal";
import { useGeneralSettings } from "@hooks/useGeneralSettings";
import * as S from "../../../store-setup/individual-store/overview/style";
import useConfig from "@hooks/useConfig";
import { useNavigate } from "react-router-dom";
import DeliveryOverviewDashboard from "./DeliveryOverviewDashboard";
import DeliveryBankDetails from "./DeliveryBankDetails";
import { Helper } from "@utils/index";
import RecentNotifications from "@components/recent-notifications";
import PushNotificationForm from "@commons/push-notifications/form";
import deliveryManApi from "@apis/deliveryman";
import { Order as OrderApi } from "@apis/index";

const Overview = ({ state }: { state: any }) => {
  const { showModal, hideModal } = useModal();
  const { settings: currencySettings } = useGeneralSettings();
  const navigate = useNavigate();
  if (!state) return <Typography>No data available</Typography>;
  const { bucketUrl } = useConfig();
  const {
    id,
    _id,
    name,
    email,
    phoneNo,
    city,
    state: driverState,
    avatar,
    backImg,
    bluebookBackImg,
    bluebookFrontImg,
    frontImg,
    identityNo,
    identityType,
    insuranceImg,
    licenseImg,
    licenseNo,
    licenseValidity,
    status,
    type,
    vehicleImg,
    vehicleType,
    zone,
    isActive,
  } = state;

  // Base URL for images
  const baseURL = bucketUrl + "/";

  const [analyticsData, setAnalyticsData] = useState<any>(null);
  const [loadingAnalytics, setLoadingAnalytics] = useState(true);

  useEffect(() => {
    const fetchAnalytics = async () => {
      try {
        setLoadingAnalytics(true);
        const res = await deliveryManApi.getAnalytics(id || _id);
        setAnalyticsData(res);
      } catch (error) {
        console.error("Failed to fetch delivery analytics", error);
      } finally {
        setLoadingAnalytics(false);
      }
    };
    if (id || _id) {
      fetchAnalytics();
    }
  }, [id, _id]);

  // Compute dynamic stats from analyticsData
  const totalDeliveries = analyticsData?.orderOverview
    ? Object.values(analyticsData.orderOverview).reduce((sum: number, val: any) => sum + (val || 0), 0) as number
    : 0;

  const ov = analyticsData?.orderOverview || {};

  const completedDeliveries = ov.Delivered || 0;
  // Orders the rider currently has on the road (picked up → out → near customer).
  const inProgressDeliveries =
    (ov["Out for Delivery"] || 0) +
    (ov["Near Customer"] || 0) +
    (ov["Order Picked Up"] || 0);
  // Assigned but not yet picked up — still waiting at the store.
  const pendingDeliveries =
    (ov["Delivery Assigned"] || 0) +
    (ov["Ready for Pickup"] || 0) +
    (ov.Pending || 0);
  const cancelledDeliveries =
    (ov.Cancelled || 0) +
    (ov.Canceled || 0) +
    (ov.Rejected || 0) +
    (ov.Failed || 0);

  const cancellationRate = totalDeliveries > 0
    ? parseFloat(((cancelledDeliveries / totalDeliveries) * 100).toFixed(1))
    : 0;

  const activeHours = totalDeliveries > 0 ? Math.round(totalDeliveries * 0.4) : 0;

  const deliveryMetrics = {
    totalDeliveries,
    monthlyGrowth: 0,
    completedDeliveries,
    activeHours,
    satisfactionRate: state?.avgRating || 0,
    onTimeRate: totalDeliveries > 0 ? parseFloat(((completedDeliveries / totalDeliveries) * 100).toFixed(1)) : 100,
    averageRating: state?.avgRating || 0,
    totalEarnings: analyticsData?.earnings?.total || 0,
    monthlyEarnings: analyticsData?.earnings?.thisMonth?.total || 0,
    cancellationRate,
  };

  const deliveryData = [
    { name: "Completed", value: completedDeliveries, color: "#10b981" },
    { name: "In Progress", value: inProgressDeliveries, color: "#3b82f6" },
    { name: "Pending", value: pendingDeliveries, color: "#f59e0b" },
    { name: "Cancelled", value: cancelledDeliveries, color: "#ef4444" },
  ];

  // Total across the four status buckets — used for the donut percentages so
  // they always sum to 100% (independent of any unrelated pre-assignment
  // statuses that may exist in orderOverview).
  const statusTotal = deliveryData.reduce((sum, d) => sum + d.value, 0);
  // Only non-zero slices are drawn so the donut has no empty padding gaps.
  const chartData = deliveryData.filter((d) => d.value > 0);
  const completionRate =
    statusTotal > 0
      ? Math.round((completedDeliveries / statusTotal) * 100)
      : 0;

  const [recentActivities, setRecentActivities] = useState<any[]>([]);
  const [loadingActivities, setLoadingActivities] = useState(true);

  const formatTimeAgo = (dateString: string) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins} min${diffMins > 1 ? "s" : ""} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? "s" : ""} ago`;
    return `${diffDays} day${diffDays > 1 ? "s" : ""} ago`;
  };

  useEffect(() => {
    const fetchActivities = async () => {
      try {
        setLoadingActivities(true);
        const res = await OrderApi.list({
          params: {
            deliveryBoyId: id || _id,
            limit: 5,
          },
        });
        const orders = res?.data || [];
        const activities = orders.map((order: any) => {
          let action = "Delivery assigned";
          let type = "assignment";
          if (order.status === "Delivered") {
            action = "Delivery completed";
            type = "delivery";
          } else if (order.status === "Out for Delivery") {
            action = "Out for delivery";
            type = "delivery";
          } else if (order.status === "Order Picked Up") {
            action = "Order picked up";
            type = "delivery";
          } else if (["Cancelled", "Canceled", "Failed"].includes(order.status)) {
            action = "Delivery cancelled";
            type = "maintenance";
          }
          return {
            action: `${action} (Order #${order.trackingId || order.id})`,
            time: formatTimeAgo(order.createdAt),
            type,
            location: order.shippingInfos?.city || order.shippingInfos?.address || "",
          };
        });
        setRecentActivities(activities);
      } catch (error) {
        console.error("Failed to fetch recent activities", error);
      } finally {
        setLoadingActivities(false);
      }
    };
    if (id || _id) {
      fetchActivities();
    }
  }, [id, _id]);

  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case "active":
        return "#10b981";
      case "pending":
        return "#f59e0b";
      case "inactive":
        return "#ef4444";
      case "approved":
        return "#3b82f6";
      default:
        return "#6b7280";
    }
  };

  const getVehicleIcon = (vehicleType: string) => {
    switch (vehicleType?.toLowerCase()) {
      case "bike":
        return <Bike size={20} />;
      case "car":
        return <Car size={20} />;
      default:
        return <Truck size={20} />;
    }
  };

  // Document viewer modal component
  const DocumentViewer = ({
    documentUrl,
    documentName,
  }: {
    documentUrl: string;
    documentName: string;
  }) => {
    const [zoom, setZoom] = useState(1);
    const [rotation, setRotation] = useState(0);

    const handleZoomIn = () => setZoom((prev) => Math.min(prev + 0.25, 3));
    const handleZoomOut = () => setZoom((prev) => Math.max(prev - 0.25, 0.5));
    const handleRotate = () => setRotation((prev) => (prev + 90) % 360);
    const handleReset = () => {
      setZoom(1);
      setRotation(0);
    };

    return (
      <S.DocumentModalContainer>
        <S.DocumentModalHeader>
          <Typography weight={600} size={18}>
            {documentName}
          </Typography>
          <S.DocumentControls>
            <S.ControlButton onClick={handleZoomOut} disabled={zoom <= 0.5}>
              <ZoomOut size={18} />
            </S.ControlButton>
            <S.ControlButton onClick={handleZoomIn} disabled={zoom >= 3}>
              <ZoomIn size={18} />
            </S.ControlButton>
            <S.ControlButton onClick={handleRotate}>
              <RotateCw size={18} />
            </S.ControlButton>
            <S.ControlButton onClick={handleReset}>Reset</S.ControlButton>
            <S.ZoomLevel>{Math.round(zoom * 100)}%</S.ZoomLevel>
          </S.DocumentControls>
        </S.DocumentModalHeader>
        <S.DocumentImageContainer>
          <S.DocumentImage
            src={baseURL + documentUrl}
            alt={documentName}
            style={{
              transform: `scale(${zoom}) rotate(${rotation}deg)`,
            }}
          />
        </S.DocumentImageContainer>
      </S.DocumentModalContainer>
    );
  };

  const handleViewDocument = (documentUrl: string, documentName: string) => {
    showModal(
      <DocumentViewer documentUrl={documentUrl} documentName={documentName} />,
      { width: "500px" },
    );
  };

  const isLicenseExpired = new Date(licenseValidity) < new Date();
  const daysUntilExpiry = Math.ceil(
    (new Date(licenseValidity).getTime() - new Date().getTime()) /
      (1000 * 60 * 60 * 24),
  );

  const DocumentsListModal = () => (
    <S.DocumentModalContainer>
      <S.DocumentModalHeader>
        <Typography weight={600} size={18}>
          Driver Documents
        </Typography>
      </S.DocumentModalHeader>
      <S.DocumentsGrid style={{ padding: "24px" }}>
        <S.DocumentItem>
          <S.DocumentIcon>
            <IdCard size={20} />
          </S.DocumentIcon>
          <S.DocumentInfo>
            <S.DocumentContent>
              <S.DocumentLabel>Driver's License</S.DocumentLabel>
              <S.DocumentStatus
                verified={!isLicenseExpired}
                pending={isLicenseExpired}
              >
                {isLicenseExpired ? "Expired" : "Verified"}
              </S.DocumentStatus>
              {!isLicenseExpired && (
                <S.MetricSubtext>
                  Expires in {daysUntilExpiry} days
                </S.MetricSubtext>
              )}
            </S.DocumentContent>
            <S.DocumentActions>
              <S.DocumentAction
                onClick={() =>
                  handleViewDocument(licenseImg, "Driver's License")
                }
              >
                View
              </S.DocumentAction>
              <S.DocumentDownload>
                <Download size={14} />
              </S.DocumentDownload>
            </S.DocumentActions>
          </S.DocumentInfo>
        </S.DocumentItem>

        <S.DocumentItem>
          <S.DocumentIcon>
            <FileText size={20} />
          </S.DocumentIcon>
          <S.DocumentInfo>
            <S.DocumentContent>
              <S.DocumentLabel>Vehicle Insurance</S.DocumentLabel>
              <S.DocumentStatus verified>Verified</S.DocumentStatus>
            </S.DocumentContent>
            <S.DocumentActions>
              <S.DocumentAction
                onClick={() =>
                  handleViewDocument(insuranceImg, "Vehicle Insurance")
                }
              >
                View
              </S.DocumentAction>
              <S.DocumentDownload>
                <Download size={14} />
              </S.DocumentDownload>
            </S.DocumentActions>
          </S.DocumentInfo>
        </S.DocumentItem>

        <S.DocumentItem>
          <S.DocumentIcon>
            <BadgeCheck size={20} />
          </S.DocumentIcon>
          <S.DocumentInfo>
            <S.DocumentContent>
              <S.DocumentLabel>Bluebook (Front)</S.DocumentLabel>
              <S.DocumentStatus verified>Verified</S.DocumentStatus>
            </S.DocumentContent>
            <S.DocumentActions>
              <S.DocumentAction
                onClick={() =>
                  handleViewDocument(bluebookFrontImg, "Bluebook Front")
                }
              >
                View
              </S.DocumentAction>
              <S.DocumentDownload>
                <Download size={14} />
              </S.DocumentDownload>
            </S.DocumentActions>
          </S.DocumentInfo>
        </S.DocumentItem>

        <S.DocumentItem>
          <S.DocumentIcon>
            <BadgeCheck size={20} />
          </S.DocumentIcon>
          <S.DocumentInfo>
            <S.DocumentContent>
              <S.DocumentLabel>Bluebook (Back)</S.DocumentLabel>
              <S.DocumentStatus verified>Verified</S.DocumentStatus>
            </S.DocumentContent>
            <S.DocumentActions>
              <S.DocumentAction
                onClick={() =>
                  handleViewDocument(bluebookBackImg, "Bluebook Back")
                }
              >
                View
              </S.DocumentAction>
              <S.DocumentDownload>
                <Download size={14} />
              </S.DocumentDownload>
            </S.DocumentActions>
          </S.DocumentInfo>
        </S.DocumentItem>
      </S.DocumentsGrid>
    </S.DocumentModalContainer>
  );

  const handleOpenDocumentsKeywords = () => {
    showModal(<DocumentsListModal />, { width: "500px" });
  };

  const handleLoadWallet = () => {
    showModal(
      <WalletAdjustmentModal
        user={state}
        onClose={hideModal}
        onSuccess={() => {
          window.location.reload();
        }}
        currencySettings={currencySettings}
      />,
    );
  };

  return (
    <S.Container>
      {/* Driver Header Section */}
      <S.StoreHeaderSection>
        <S.CoverImageContainer
          style={{
            background:
              "linear-gradient(135deg, #fce4ec 0%, #f8bbd0 30%, #ef9a9a 60%, #e57373 100%)",
            minHeight: 280,
          }}
        >
          <S.HeaderOverlay
            style={{
              background:
                "linear-gradient(to top, rgba(0,0,0,0.6), transparent)",
            }}
          />

          <S.CoverActionIcon
            onClick={handleOpenDocumentsKeywords}
            title="View Documents"
          >
            <FileText size={24} />
          </S.CoverActionIcon>

          <S.StoreHeaderContent>
            <S.AvatarContainer>
              {avatar ? (
                <S.AvatarImage src={baseURL + avatar} alt="Driver avatar" />
              ) : (
                <S.AvatarPlaceholder
                  bg={Helper.getDynamicColor(name || "Driver")}
                >
                  {Helper.getInitials(name || "Driver")}
                </S.AvatarPlaceholder>
              )}
              <S.OnlineIndicator />
            </S.AvatarContainer>
            <S.StoreInfo>
              <S.StoreNameRow>
                <Typography weight={700} size={28} color="white">
                  {name}
                </Typography>
                <S.BadgeContainer>
                  {isActive && <S.FeaturedBadge>Active</S.FeaturedBadge>}
                  <S.PopularBadge
                    style={{ backgroundColor: getStatusColor(status) }}
                  >
                    {status}
                  </S.PopularBadge>
                  <S.VerificationBadge>
                    <ShieldCheck size={14} />
                    Verified
                  </S.VerificationBadge>
                </S.BadgeContainer>
              </S.StoreNameRow>

              <S.StoreMeta>
                <S.MetaItem>
                  <Star size={16} color="#ffd700" />
                  <Typography size={14} color="white">
                    {deliveryMetrics.averageRating}/5 (
                    {deliveryMetrics.totalDeliveries} deliveries)
                  </Typography>
                </S.MetaItem>
                <S.MetaItem>
                  {getVehicleIcon(vehicleType)}
                  <Typography size={14} color="white">
                    {vehicleType} • {type}
                  </Typography>
                </S.MetaItem>
                <S.MetaItem>
                  <MapPin size={16} color="white" />
                  <Typography size={14} color="white">
                    {city}, {zone?.name}
                  </Typography>
                </S.MetaItem>
                <S.MetaItem>
                  <Award size={16} color="white" />
                  <Typography size={14} color="white">
                    {deliveryMetrics.onTimeRate}% On-time
                  </Typography>
                </S.MetaItem>
              </S.StoreMeta>

              <S.ActionButtons>
                <S.ActionButton primary>
                  <MessageCircle size={16} />
                  Contact Driver
                </S.ActionButton>
                <S.ActionButton
                  onClick={() =>
                    navigate(
                      `/admin/users/delivery-man/update-delivery-man/${state.id}`,
                      { state },
                    )
                  }
                >
                  <Edit size={16} />
                  Edit Info
                </S.ActionButton>
                <S.ActionButton
                  onClick={() => {
                    showModal(
                      <PushNotificationForm
                        initialData={{
                          targetAudience: "specific_delivery",
                          targetId: state.id,
                          title: `Delivery Notification: ${name}`,
                        }}
                        onSuccess={() => hideModal()}
                      />,
                      { width: "1000px" },
                    );
                  }}
                >
                  <Bell size={16} />
                  Send Push
                </S.ActionButton>
                <S.ActionButton
                  onClick={() => {
                    navigate(`/admin/chat?userId=${state.id}&role=delivery`);
                  }}
                >
                  <MessageCircle size={16} />
                  Chat
                </S.ActionButton>
                <S.ActionButton onClick={handleLoadWallet}>
                  <Wallet size={16} />
                  Load Wallet
                </S.ActionButton>
              </S.ActionButtons>
            </S.StoreInfo>
          </S.StoreHeaderContent>
        </S.CoverImageContainer>
      </S.StoreHeaderSection>

      {/* Main Content Grid */}
      <S.ContentGrid>
        {/* Left Column - Driver Details */}
        <S.LeftColumn>
          <DeliveryOverviewDashboard deliveryManId={id || _id} />
          <DeliveryBankDetails deliveryManId={id || _id} />
        </S.LeftColumn>

        {/* Right Column */}
        <S.RightColumn>
          {/* Recent Notifications */}
          <S.Section>
            <RecentNotifications userId={id || _id} role="delivery" />
          </S.Section>

          {/* Driver Information */}
          <S.Section>
            <S.SectionHeader>
              <Typography weight={600} size={18}>
                Driver Information
              </Typography>
            </S.SectionHeader>
            <S.InfoGrid>
              <S.InfoItem>
                <S.InfoIcon>
                  <UserCheck size={16} />
                </S.InfoIcon>
                <S.InfoContent>
                  <S.InfoLabel>Driver Since</S.InfoLabel>
                  <S.InfoValue>March 2023</S.InfoValue>
                </S.InfoContent>
              </S.InfoItem>
              <S.InfoItem>
                <S.InfoIcon>
                  <Bike size={16} />
                </S.InfoIcon>
                <S.InfoContent>
                  <S.InfoLabel>Vehicle Type</S.InfoLabel>
                  <S.InfoValue>{vehicleType}</S.InfoValue>
                </S.InfoContent>
              </S.InfoItem>
              <S.InfoItem>
                <S.InfoIcon>
                  <CreditCard size={16} />
                </S.InfoIcon>
                <S.InfoContent>
                  <S.InfoLabel>License No</S.InfoLabel>
                  <S.InfoValue>{licenseNo}</S.InfoValue>
                </S.InfoContent>
              </S.InfoItem>
              <S.InfoItem>
                <S.InfoIcon>
                  <MapPin size={16} />
                </S.InfoIcon>
                <S.InfoContent>
                  <S.InfoLabel>Service Area</S.InfoLabel>
                  <S.InfoValue>{zone?.name}</S.InfoValue>
                </S.InfoContent>
              </S.InfoItem>
            </S.InfoGrid>
          </S.Section>

          {/* Delivery Distribution */}
          <S.Section>
            <S.SectionHeader>
              <Typography weight={600} size={18}>
                Delivery Status
              </Typography>
            </S.SectionHeader>
            <S.ChartContainer>
              {statusTotal > 0 ? (
                <>
                  <div style={{ position: "relative", width: "100%" }}>
                    <ResponsiveContainer width="100%" height={200}>
                      <PieChart>
                        <Pie
                          data={chartData}
                          cx="50%"
                          cy="50%"
                          innerRadius={60}
                          outerRadius={80}
                          paddingAngle={chartData.length > 1 ? 4 : 0}
                          dataKey="value"
                          stroke="none"
                        >
                          {chartData.map((entry) => (
                            <Cell key={entry.name} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip
                          formatter={(value: any, name: any) => [
                            `${value} order${value === 1 ? "" : "s"} (${Math.round(
                              (Number(value) / statusTotal) * 100,
                            )}%)`,
                            name,
                          ]}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                    {/* Center total overlay */}
                    <div
                      style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        height: 200,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        pointerEvents: "none",
                      }}
                    >
                      <Typography weight={800} size={28} color="#0f172a">
                        {statusTotal}
                      </Typography>
                      <Typography size={12} color="#64748b">
                        Total Orders
                      </Typography>
                    </div>
                  </div>
                  <S.OrderLegend>
                    {deliveryData.map((item) => (
                      <S.LegendItem key={item.name}>
                        <S.LegendColor color={item.color} />
                        <S.LegendText>
                          {item.name}: {item.value} (
                          {Math.round((item.value / statusTotal) * 100)}%)
                        </S.LegendText>
                      </S.LegendItem>
                    ))}
                  </S.OrderLegend>
                  <div
                    style={{
                      marginTop: 12,
                      paddingTop: 12,
                      borderTop: "1px solid #f1f5f9",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Typography size={13} color="#64748b">
                      Completion Rate
                    </Typography>
                    <Typography weight={700} size={15} color="#10b981">
                      {completionRate}%
                    </Typography>
                  </div>
                </>
              ) : (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "200px",
                    color: "#64748b",
                  }}
                >
                  <Typography size={14} color="#64748b">
                    No delivery data available
                  </Typography>
                </div>
              )}
            </S.ChartContainer>
          </S.Section>

          {/* Recent Activities */}
          <S.Section>
            <S.SectionHeader>
              <Typography weight={600} size={18}>
                Recent Activities
              </Typography>
            </S.SectionHeader>
            <S.ActivitiesList>
              {loadingActivities ? (
                <div style={{ display: "flex", flexDirection: "column", gap: "12px", padding: "8px 0" }}>
                  <div style={{ width: "100%", height: "50px", background: "#f3f4f6", borderRadius: "8px", animation: "pulse 1.5s infinite" }} />
                  <div style={{ width: "100%", height: "50px", background: "#f3f4f6", borderRadius: "8px", animation: "pulse 1.5s infinite" }} />
                  <div style={{ width: "100%", height: "50px", background: "#f3f4f6", borderRadius: "8px", animation: "pulse 1.5s infinite" }} />
                </div>
              ) : recentActivities.length > 0 ? (
                recentActivities.map((activity, index) => (
                  <S.ActivityItem key={index}>
                    <S.ActivityIcon type={activity.type}>
                      {activity.type === "delivery" && <Package size={14} />}
                      {activity.type === "assignment" && (
                        <ShoppingCart size={14} />
                      )}
                      {activity.type === "rating" && <Star size={14} />}
                      {activity.type === "payment" && <DollarSign size={14} />}
                      {activity.type === "maintenance" && <Truck size={14} />}
                    </S.ActivityIcon>
                    <S.ActivityContent>
                      <S.ActivityText>{activity.action}</S.ActivityText>
                      <S.ActivityTime>
                        {activity.time}{" "}
                        {activity.location && `• ${activity.location}`}
                      </S.ActivityTime>
                    </S.ActivityContent>
                  </S.ActivityItem>
                ))
              ) : (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: "24px 0",
                    color: "#64748b",
                  }}
                >
                  <Typography size={14} color="#64748b">
                    No recent activities found
                  </Typography>
                </div>
              )}
            </S.ActivitiesList>
          </S.Section>
        </S.RightColumn>
      </S.ContentGrid>
    </S.Container>
  );
};

export default Overview;
